import React, { Fragment } from 'react';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import { constants } from '../../../config';
import { ComplexRadioBullet } from '../../../common/complex-radio-bullet';
import { Select } from '../../../common/select';
import { NumberHelper } from '../../../helpers/NumberHelper';
import { OPTION_TYPES } from '../../../models/ShippingOption';
import { ScreenStore } from '../../../stores/ScreenStore';
import styles from './ShippingRadio.css';

const ITimeSlots = PropTypes.arrayOf(PropTypes.shape({
  id: PropTypes.string,
  date: PropTypes.instanceOf(DateTime),
  startHour: PropTypes.number,
  endHour: PropTypes.number,
}));

const ISimpleOption = PropTypes.shape({
  date: PropTypes.instanceOf(DateTime),
  endHour: PropTypes.number,
  id: PropTypes.string,
  optionType: PropTypes.oneOf([OPTION_TYPES.SIMPLE]),
  price: PropTypes.number,
  startHour: PropTypes.number,
});

const ISpecialOption = PropTypes.shape({
  id: PropTypes.string,
  optionType: PropTypes.oneOf([OPTION_TYPES.SPECIAL]),
  timeSlots: ITimeSlots,
});

const IAgencyOption = PropTypes.shape({
  id: PropTypes.string,
  price: PropTypes.number,
  name: PropTypes.string,
  optionType: PropTypes.oneOf([OPTION_TYPES.AGENCY]),
  agencies: PropTypes.arrayOf(PropTypes.shape({
    extraCost: PropTypes.number,
    id: PropTypes.id,
    name: PropTypes.string,
  })),
  timeSlots: ITimeSlots,
});

const IPropTypes = {
  option: PropTypes.oneOfType([
    IAgencyOption,
    ISimpleOption,
    ISpecialOption,
  ]).isRequired,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};

const defaultProps = {
  onClick: () => {},
  selected: false,
};

const formatDate = date => (
  `${date.toFormat('cccc d \'de\' MMMM')}`
);

class ShippingRadio extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTimeSlot: null,
      selectedAgency: null,
    };

    this._onClickHandler = this._onClickHandler.bind(this);
    this._onSelectFirstField = this._onSelectFirstField.bind(this);
    this._onSelectSecondField = this._onSelectSecondField.bind(this);
  }

  _onClickHandler() {
    this.props.onClick(this.props.option, this.state.selectedTimeSlot, this.state.selectedAgency);
  }

  _onSelectFirstField(value) {
    const timeSlot = this.props.option.timeSlots.find(slot => slot.id === value);
    this.setState({
      selectedTimeSlot: timeSlot,
    });
    this.props.onClick(this.props.option, timeSlot, this.state.selectedAgency);
  }

  _onSelectSecondField(value) {
    const agency = this.props.option.agencies.find(a => a.id === value);
    this.setState({
      selectedAgency: agency,
    });
    this.props.onClick(this.props.option, this.state.selectedTimeSlot, agency);
  }

  _renderTopMessage() {
    if (this.props.option.optionType === OPTION_TYPES.SIMPLE) {
      return (
        <Fragment>
          <p className={styles.text}>
            Envío común
          </p>
          <p className={styles.subtext}>
            Se recibe el {this.props.option.futureDate
             ? formatDate(this.props.option.futureDate) : formatDate(this.props.option.date)} (
            {this.props.option.startHourString}hs a {this.props.option.endHourString}hs)
          </p>
        </Fragment>
      );
    } else if (this.props.option.optionType === OPTION_TYPES.SPECIAL) {
      return (
        <Fragment>
          <p className={styles.text}>
            Envío a hora personalizada
          </p>
        </Fragment>
      );
    } else if (this.props.option.optionType === OPTION_TYPES.AGENCY) {
      return (
        <Fragment>
          <p className={styles.text}>
            Envío por agencia - {this.props.option.name}
          </p>
        </Fragment>
      );
    }
    return null;
  }

  _renderBody() {
    if (this.props.option.optionType === OPTION_TYPES.SIMPLE) {
      return null;
    } else if (this.props.option.optionType === OPTION_TYPES.SPECIAL) {
      const firstFieldOptions = this.props.option.timeSlots.map(slot => ({
        label: `${slot.futureDate
          ? formatDate(slot.futureDate) : formatDate(slot.date)} - ${slot.startHourString}hs a ${slot.endHourString}hs`,
        value: slot.id,
      }));
      const selectedValue = firstFieldOptions.find(option => (
        this.state.selectedTimeSlot && option.value === this.state.selectedTimeSlot.id
      ));
      return (
        <Select
          className={styles.select}
          options={firstFieldOptions}
          optionClassName={styles.selectText}
          selectedClassName={styles.selectText}
          onChange={this._onSelectFirstField}
          disabled={!this.props.selected}
          value={selectedValue ? selectedValue.label : 'Seleccionar'}
        />
      );
    } else if (this.props.option.optionType === OPTION_TYPES.AGENCY) {
      const firstFieldOptions = this.props.option.timeSlots.map(slot => ({
        label: `${slot.futureDate
          ? formatDate(slot.futureDate) : formatDate(slot.date)} - ${slot.startHourString}hs a ${slot.endHourString}hs`,
        value: slot.id,
      }));
      const firstFieldSelectedValue = firstFieldOptions.find(option => (
        this.state.selectedTimeSlot && option.value === this.state.selectedTimeSlot.id
      ));
      const secondFieldOptions = this.props.option.agencies.map(agency => ({
        label: agency.name,
        value: agency.id,
      }));
      const secondFieldSelectedValue = secondFieldOptions.find(option => (
        this.state.selectedAgency && option.value === this.state.selectedAgency.id
      ));
      return (
        <Fragment>
          <p className={styles.subtext}>
            Agencia
          </p>
          <Select
            className={styles.select}
            options={secondFieldOptions}
            optionClassName={styles.selectText}
            selectedClassName={styles.selectText}
            onChange={this._onSelectSecondField}
            value={secondFieldSelectedValue ? secondFieldSelectedValue.label : 'Seleccionar'}
          />
          <p className={styles.subtext}>
            Fecha de despacho
          </p>
          <Select
            className={styles.select}
            options={firstFieldOptions}
            optionClassName={styles.selectText}
            selectedClassName={styles.selectText}
            onChange={this._onSelectFirstField}
            value={firstFieldSelectedValue ? firstFieldSelectedValue.label : 'Seleccionar'}
          />
        </Fragment>
      );
    }
    return null;
  }

  _renderRightContent() {
    const isMobile = ScreenStore.screenWidth <= constants.breakpoints.mobileThreshold;
    if (!isMobile && this.props.option.optionType === OPTION_TYPES.AGENCY) {
      return <div style={{ marginTop: 55 }}>costo a definir por agencia</div>;
    } else if (this.props.option.optionType !== OPTION_TYPES.AGENCY) {
      return <div>{`$${NumberHelper.localizeNumber(this.props.option.price)}`}</div>;
    }
    return null;
  }

  render() {
    return (
      <ComplexRadioBullet
        body={this._renderBody()}
        onClick={this._onClickHandler}
        rightContent={this._renderRightContent()}
        selected={this.props.selected}
        topMessage={this._renderTopMessage()}
      />
    );
  }
}

ShippingRadio.propTypes = IPropTypes;
ShippingRadio.defaultProps = defaultProps;

const ObservedShippingRadio = observer(ShippingRadio);

export { ObservedShippingRadio as ShippingRadio };



// WEBPACK FOOTER //
// ./src/pages/shipping-options/shipping-radio/ShippingRadio.jsx